import type { LintFinding } from '@ralph/contracts'
import clsx from 'clsx'
import { CircleAlert, Lightbulb } from 'lucide-react'
import { describeIssue, useT } from '@/i18n/index.ts'

/**
 * Server lint findings, rendered as notes rather than errors.
 *
 * The distinction the maker has to see is blocking versus suggestion, and it is carried by
 * an icon, a heading and a word — never by colour alone (AGM-124). Under the friendly heading
 * sits the finding itself, which knows the specifics (which field, which claim) that a
 * client-side heading keyed by the coarse lint code cannot.
 *
 * It used to sit there in English. A maker whose publish was REFUSED read their own language,
 * because a refusal travels as an `ApiIssue` and has carried a `messageKey` since the server
 * seam landed; the same maker, merely warned, read English from the same lint run — the
 * advisory findings never became refusals, so nothing ever keyed them (PRD-27 LOC-033). Every
 * finding `lintListingProse` returns now carries its key, and the stored prose is the fallback
 * for the one finding the restore route assembles itself.
 */
export const LintNotes = ({ findings, className }: { findings: LintFinding[]; className?: string }) => {
  // Before the early return, because it is a hook; and inside the body, because a module-scope
  // capture of a catalogue branch renders the previous language with no error (PRD-27 F-3).
  // The whole catalogue rather than one branch of it: the notes need their own words AND the
  // server's sentences, and the second lookup goes through `describeIssue`.
  const t = useT()
  const strings = t.marketplace.maker.lint
  if (findings.length === 0) return null

  return (
    <ul className={clsx('space-y-2', className)}>
      {findings.map((finding) => (
        <li
          // Code and field, without the prose: the lint reports one finding per code per
          // field, so the pair is already the identity, and the sentence has stopped being
          // one — it is chosen by the reader's locale now.
          key={`${finding.code}:${finding.field}`}
          className={clsx(
            'flex items-start gap-2.5 rounded-tile border px-3 py-2.5',
            finding.blocking ? 'border-alert/40 bg-alert-soft' : 'border-rule bg-card-solid'
          )}
        >
          <span
            className={clsx(
              'mt-px flex h-[22px] w-[22px] flex-none items-center justify-center rounded-[7px] border border-rule',
              finding.blocking ? 'bg-alert-soft text-alert' : 'bg-card-solid text-fg-muted'
            )}
            aria-hidden
          >
            {finding.blocking ? <CircleAlert size={12} strokeWidth={2.2} /> : <Lightbulb size={12} strokeWidth={2.2} />}
          </span>
          <span className="min-w-0">
            <span className="flex flex-wrap items-baseline gap-x-2">
              {/* The map is keyed by the contract's lint enum, so there is nothing to fall back
                  to: a new code is a compile error rather than `contradicts_platform` on screen. */}
              <span className="text-[13px] font-semibold tracking-[-0.014em]">{strings.heading[finding.code]}</span>
              {/* The severity is a word as well as a colour: a red card and a grey one look
                  the same to a reader who cannot tell them apart (AGM-124). */}
              <span className="eyebrow text-fg-subtle">{finding.blocking ? strings.blocking : strings.suggestion}</span>
            </span>
            <span className="mt-1 block text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
              {describeIssue(t, finding) ?? finding.message}
            </span>
          </span>
        </li>
      ))}
    </ul>
  )
}

export const findingsFor = (findings: LintFinding[], ...fields: string[]): LintFinding[] =>
  findings.filter((finding) => fields.includes(finding.field))
